import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Hourglass, FlaskConical } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ReferenceLine, CartesianGrid } from "recharts";
import { api, errMsg } from "@/lib/api";
import { AppShell, PageTitle } from "@/components/AppShell";
import { SourceChip } from "@/components/Chips";
import { Button } from "@/components/ui/button";

const Pct = ({ v, testid }) => (
  <span data-testid={testid} className={`font-mono text-2xl ${v == null ? "text-muted-foreground" : v >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
    {v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`}
  </span>
);

const WindowCard = ({ w, source }) => (
  <div data-testid={`proof-window-${w.days}`} className="rounded-2xl border glass p-5">
    <div className="flex items-center justify-between"><p className="font-display text-lg">{w.days} days</p><SourceChip label={source} testid={`proof-window-${w.days}-source`} /></div>
    {!w.available ? (
      <p data-testid={`proof-window-${w.days}-collecting`} className="mt-4 text-sm text-muted-foreground">Collecting data — check back in {w.check_back_in} days.</p>
    ) : (
      <div className="mt-4 grid grid-cols-2 gap-4">
        {[["Views", "views"], ["Favorites", "favs"]].map(([label, k]) => (
          <div key={k}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Avg {label.toLowerCase()} change</p>
            <div className="mt-2 space-y-1">
              <div className="flex items-baseline justify-between gap-2"><span className="text-xs">Edited</span><Pct v={w[`edited_${k}`]} testid={`proof-${w.days}-edited-${k}`} /></div>
              <div className="flex items-baseline justify-between gap-2"><span className="text-xs text-muted-foreground">Untouched</span><Pct v={w[`untouched_${k}`]} testid={`proof-${w.days}-untouched-${k}`} /></div>
            </div>
          </div>
        ))}
        <p className="col-span-2 font-mono text-[11px] text-muted-foreground">{w.edited_n} edited vs {w.untouched_n} untouched listings · {w.days}d before vs after edit</p>
      </div>
    )}
  </div>
);

export default function Proof() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/proof").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const simulate = async () => {
    setBusy(true);
    try { await api.post("/demo/simulate-days", { days: 30 }); toast.success("Simulated 30 days of post-edit demo stats."); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <PageTitle eyebrow="Proof view" title="Edited vs untouched listings" testid="proof-title">
        {data?.shop_kind === "demo" && data.state !== "no_edits" && (
          <Button variant="outline" className="rounded-full border-[#E9C98B]/45 bg-[#E9C98B]/10" onClick={simulate} disabled={busy} data-testid="simulate-days-button">
            <FlaskConical className="h-4 w-4 mr-1" />{busy ? "Simulating…" : "Simulate 30 days (demo only)"}
          </Button>
        )}
      </PageTitle>
      <p data-testid="proof-disclaimer" className="mb-8 rounded-xl border bg-secondary/50 px-4 py-3 text-sm">Results are associated with changes, not guaranteed.</p>
      {!data ? <div className="h-64 rounded-2xl shimmer" /> : data.state === "no_edits" ? (
        <div data-testid="proof-no-edits" className="rounded-2xl border glass p-8">
          <p className="font-display text-xl">No edits yet</p>
          <p className="mt-2 text-sm text-muted-foreground">Push your first fix and we'll start comparing it with listings you left alone.</p>
          <Button asChild className="mt-4 rounded-full" data-testid="proof-go-catalog"><Link to="/app">Go to catalog</Link></Button>
        </div>
      ) : (
        <>
          {data.state === "collecting" && (
            <div data-testid="proof-collecting" className="mb-6 flex items-center gap-3 rounded-2xl border border-primary/30 bg-accent/50 p-5">
              <Hourglass className="h-5 w-5 text-primary" />
              <p className="text-sm" data-testid="proof-collecting-message">Collecting data — check back in {data.check_back_in} days.</p>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-3">{data.windows.map((w) => <WindowCard key={w.days} w={w} source={data.source} />)}</div>
          <section data-testid="proof-chart" className="mt-8 rounded-2xl border glass p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-xl font-medium">Avg daily views per listing</h2>
              <SourceChip label={data.source} testid="proof-chart-source" />
            </div>
            <p className="mt-1 text-xs text-muted-foreground font-mono">{data.edited_count} edited · {data.untouched_count} untouched</p>
            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.chart} margin={{ left: -20, right: 8 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} minTickGap={24} stroke="#6E6F8F" tickLine={false} axisLine={false} />
                  <YAxis stroke="#6E6F8F" tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12, background: "#0E1020", border: "1px solid rgba(255,255,255,0.1)", color: "#F4F1EA" }} />
                  <Legend />
                  {data.median_edit_date && <ReferenceLine x={data.median_edit_date} stroke="#E9C98B" strokeDasharray="4 4" label={{ value: "edit", fontSize: 11, fill: "#E9C98B" }} />}
                  <Line type="monotone" dataKey="edited" name="Edited" stroke="#5EE6D0" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="untouched" name="Untouched" stroke="#8B7CFF" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}
    </AppShell>
  );
}
